import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Background from '../components/Background';
import Button from '../components/Button';
import api from '../services/api';

const ReadingReplay: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/session/${id}`);
        setData(res.data);
      } catch (e: any) {
        setError(e?.response?.data?.detail || e.message || 'Failed to load session');
      } finally {
        setLoading(false);
      }
    };
    if (id) load();
  }, [id]);

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ backgroundColor: '#050508' }}>
      <Background />
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="flex items-center justify-between mb-6 sm:mb-8 animate-fade-in">
          <h1 className="text-3xl sm:text-4xl font-heading text-gradient-gold tracking-luxury font-semibold">Reading</h1>
          <Button variant="secondary" onClick={() => navigate('/profile/history')}>Back</Button>
        </div>
        {loading && (
          <div className="text-text-secondary font-elegant tracking-luxury-tight text-sm sm:text-base">Loading…</div>
        )}
        {error && (
          <div 
            className="glass-grain rounded-2xl px-6 py-5 backdrop-blur-xl border shadow-2xl text-red-400 animate-fade-in"
            style={{
              borderColor: 'rgba(239, 68, 68, 0.3)',
              backgroundColor: 'rgba(20, 20, 32, 0.3)',
              boxShadow: '0 0 40px rgba(239, 68, 68, 0.15), 0 8px 32px rgba(0,0,0,0.4)'
            }}
          >
            <p className="font-elegant text-sm sm:text-base tracking-luxury-tight">{error}</p>
          </div>
        )}
        {!loading && !error && data && (
          <div className="space-y-4 animate-fade-in">
            <div 
              className="glass-grain rounded-2xl px-6 py-5 backdrop-blur-xl border shadow-2xl"
              style={{
                borderColor: 'rgba(255, 184, 79, 0.2)',
                backgroundColor: 'rgba(20, 20, 32, 0.3)',
                boxShadow: '0 0 40px rgba(255, 184, 79, 0.15), 0 8px 32px rgba(0,0,0,0.4)'
              }}
            >
              <div className="text-text-tertiary font-elegant text-xs uppercase mb-2 tracking-luxury-tight">{data.session_type}</div>
              <div className="text-text-primary font-heading text-lg sm:text-xl mb-2 tracking-luxury-tight font-semibold">"{data.initial_question}"</div>
              <div className="text-text-tertiary font-elegant text-xs tracking-luxury-tight">Started: {new Date(data.created_at).toLocaleString()}</div>
            </div>
            {data.messages.map((m: any) => (
              <div 
                key={m.id} 
                className="glass-grain rounded-2xl px-6 py-5 backdrop-blur-xl border shadow-2xl"
                style={{
                  borderColor: m.message_kind?.startsWith('user') 
                    ? 'rgba(255, 184, 79, 0.3)' 
                    : 'rgba(255, 184, 79, 0.2)',
                  backgroundColor: m.message_kind?.startsWith('user')
                    ? 'rgba(255, 184, 79, 0.08)'
                    : 'rgba(20, 20, 32, 0.3)',
                  boxShadow: '0 0 40px rgba(255, 184, 79, 0.15), 0 8px 32px rgba(0,0,0,0.4)'
                }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div 
                    className="w-3 h-3 rounded-full"
                    style={{ 
                      backgroundColor: m.message_kind?.startsWith('user') 
                        ? 'rgba(255, 184, 79, 0.8)' 
                        : '#E5E7EB',
                      boxShadow: `0 0 12px ${m.message_kind?.startsWith('user') ? 'rgba(255, 184, 79, 0.8)' : 'rgba(229, 231, 235, 0.6)'}`
                    }}
                  />
                  <span className="text-text-primary font-heading text-sm tracking-luxury-tight font-medium">
                    {m.agent_name ? `${m.agent_name === 'Moderator' ? 'Aivaris' : m.agent_name}` : (m.message_kind?.startsWith('user') ? 'You' : 'Assistant')}
                  </span>
                </div>
                <div className="text-text-secondary font-elegant text-sm sm:text-base leading-relaxed whitespace-pre-wrap tracking-luxury-tight">{m.content}</div>
                <div className="text-text-tertiary font-elegant text-xs mt-3 tracking-luxury-tight">{new Date(m.created_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReadingReplay;


